/**
 * Copyright (C) 2026 Jugendstil, SLU - All Rights Reserved
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited. Proprietary and confidential.
 */

/**
 * Reads the `yyyy-MM-dd` an `<input type="date">` holds.
 *
 * Returns null for anything else, including the empty string a cleared field has, which is what
 * lets every caller treat "no date" and "nonsense in the field" the same way.
 *
 * Noon, not midnight. A date built at midnight and then moved by whole days lands an hour either
 * side of itself across a daylight-saving boundary, which is how a calendar grows or loses a day
 * for one week of the year.
 */
export function dateFromValue(value: string | null | undefined): Date | null {
    if (value === null || value === undefined || !/^\d{4}-\d{2}-\d{2}$/.test(value)) {
        return null;
    }

    /* Sliced rather than split: the pattern above has already fixed where each field is, and
       slicing says so in the types instead of handing three possibly-absent array entries to
       `Number`. */
    const year = Number(value.slice(0, 4));
    const month = Number(value.slice(5, 7));
    const day = Number(value.slice(8, 10));

    return new Date(year, month - 1, day, 12);
}

/** The inverse, in the one format `<input type="date">` accepts. */
export function valueFromDate(date: Date): string {
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${date.getFullYear()}-${month}-${day}`;
}

/** The `yyyy-MM` a calendar keeps on itself to remember which month is on screen. */
export function monthFromDate(date: Date): string {
    return valueFromDate(date).slice(0, 7);
}
